import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useStoreState } from 'easy-peasy';
import { useMemo } from 'react';
import { toast } from 'react-toastify';
import { api } from '~/api';
import { useTradableProducts } from './useProducts';

// Types
export interface Preset {
  id: string;
  name: string;
  product: {
    id: string;
  };
  sell: boolean;
  [key: string]: any; // Form fields
}

interface CreatePresetData {
  name: string;
  product: string;
  sell: boolean;
  [key: string]: any; // Form fields from formWrapper.serialize()
}

// Query Hook
export const usePresets = (solutionId?: string) => {
  const solution = useStoreState((state: any) => state.auth.solution);
  const effectiveSolutionId = solutionId ?? solution?.id;

  return useQuery({
    queryKey: ['presets', effectiveSolutionId],
    queryFn: async () => {
      if (!effectiveSolutionId) return [];
      const presets = await api.getData(
        { type: 'presets' },
        { solution: effectiveSolutionId }
      );
      return presets as Preset[];
    },
    enabled: !!effectiveSolutionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Create Mutation
export const useCreatePreset = () => {
  const queryClient = useQueryClient();
  const solution = useStoreState((state: any) => state.auth.solution);

  return useMutation({
    mutationFn: async (data: CreatePresetData) => {
      return await api.postData('/presets', data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['presets', solution?.id] });
      toast.success(`Preset "${variables.name}" is saved`);
    },
    onError: (error: any) => {
      const errors = error?.response?.data?.errors?.[0]?.description;
      if (errors && typeof errors === 'string') {
        toast.error(errors);
      } else {
        toast.error('An error occurred. Please try again later.');
      }
    },
  });
};

// Delete Mutation
export const useDeletePreset = () => {
  const queryClient = useQueryClient();
  const solution = useStoreState((state: any) => state.auth.solution);

  return useMutation({
    mutationFn: async (presetId: string) => {
      return await api.deleteData(`/presets/${presetId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['presets', solution?.id] });
    },
  });
};

// Filtered Hook for Tradable Products
export const useTradablePresets = (
  orderType: 'bid' | 'offer',
  solutionId?: string
) => {
  const { data: presets = [], ...rest } = usePresets(solutionId);
  const tradableProducts = useTradableProducts(orderType, solutionId);

  const filteredPresets = useMemo(
    () =>
      presets.filter((preset) =>
        tradableProducts.some((p) => p.id === preset.product.id)
      ),
    [presets, tradableProducts]
  );

  return { data: filteredPresets, ...rest };
};
